import { FormikProvider, useFormik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import IncidentTypePriorityLabel from '@/modules/common/components/IncidentTypePriority.tsx';
import DepartmentsSelectPaginated from '@/modules/admin-dashboard/components/DepartmentsSelectPaginated.tsx';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import {
  useCreateIncidentTypeMutation,
  useUpdateIncidentTypeMutation,
} from '@/modules/admin-dashboard/queries/incidents.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import {
  CreateIncidentTypeRequest,
  CreateIncidentTypeResponse,
  IncidentTypePriority,
  UpdateIncidentTypeRequest,
} from 'compliance-api';

const priorityOptions = Object.values(IncidentTypePriority).map(priority => ({
  label: <IncidentTypePriorityLabel priority={priority} />,
  value: priority,
}));

const initialValues: CreateIncidentTypeRequest = {
  name: '',
  responsible_department_id: 0,
  completion_form_id: undefined,
  priority: IncidentTypePriority.Normal,
  form_id: 0,
};

const validationSchema = yup.object().shape({
  name: yup.string().trim().min(3).max(50).required('Name is required'),
  responsible_department_id: yup
    .number()
    .notOneOf([0], 'Department is required')
    .required('Department is required'),
  priority: yup.string().required('Priority is required'),
  form_id: yup.number().notOneOf([0], 'Form is required').required('Form is required'),
  completion_form_id: yup
    .number()
    .nullable()
    .test(
      'not-same-as-form',
      'Resolve form must be different from Form',
      function (value, { parent }) {
        const { form_id } = parent;
        if (value == null) return true;
        return value !== form_id;
      },
    ),
});

interface CreateIncidentTypeModalProps extends BaseModalProps {
  defaultValues?: UpdateIncidentTypeRequest;
  onSuccess?: (result: CreateIncidentTypeResponse) => void;
  defaultPaginateOptions?: {
    form: SelectPaginateOption<number>;
    department: SelectPaginateOption<number>;
    resolve_form?: SelectPaginateOption<number>;
  };
}

const IncidentTypeModal = ({
  open,
  defaultValues,
  onSuccess,
  defaultPaginateOptions,
  onClose,
}: CreateIncidentTypeModalProps) => {
  const [department, setDepartment] = useState(defaultPaginateOptions?.department);
  const isUpdating = !!defaultValues;

  const { mutateAsync: createIncident, isLoading: isCreatingIncident } =
    useCreateIncidentTypeMutation();
  const { mutateAsync: updateIncident, isLoading: isUpdatingIncident } =
    useUpdateIncidentTypeMutation();

  const isLoading = isCreatingIncident || isUpdatingIncident;

  const formik = useFormik<CreateIncidentTypeRequest | UpdateIncidentTypeRequest>({
    initialValues: defaultValues || initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async values => {
      if ('id' in values) {
        await updateIncident(values);
      } else {
        const res = await createIncident(values);
        onSuccess && onSuccess(res);
      }
      onClose();
    },
  });

  const handleSelectDepartment = useCallback(
    (option: SelectPaginateOption<number>) => {
      setDepartment(option);
      formik.setFieldValue('responsible_department_id', option.value);
      formik.setFieldError('responsible_department_id', '');
    },
    [formik],
  );

  const handleSelectOption = useCallback(
    (option: SelectPaginateOption<number>, fieldName: string) => {
      formik.setFieldValue(fieldName, option?.value || undefined);
      formik.setFieldError(fieldName, '');
    },
    [formik],
  );

  const handleSelectPriority = useCallback(
    (newValue: IncidentTypePriority | null) => {
      if (newValue) {
        formik.setFieldValue('priority', newValue);
        formik.setFieldError('priority', '');
      }
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      title={defaultPaginateOptions?.form ? 'Edit event type' : 'Add new event type'}
      open={open}
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-8">
          <TextInputField name="name" label="Event type name" placeholder="Name..." />
          <div className="space-y-2">
            <DepartmentsSelectPaginated
              enableCreation
              label="Responsible department"
              isMulti={false}
              value={department}
              defaultValue={defaultPaginateOptions?.department}
              formFieldTitle="responsible_department_id"
              handleSelectDepartments={handleSelectDepartment}
              error={formik.errors.responsible_department_id}
            />
            <p className="text-neutral-500 text-xs">
              Members of this department will get notifications about event and will be responsible
              for creation of follow up tasks
            </p>
          </div>
          <Select<IncidentTypePriority>
            label="Priority"
            value={formik.values.priority}
            options={priorityOptions}
            onChange={handleSelectPriority}
            error={formik.errors.priority}
          />
          <FormSelectPaginate
            label="Report Form"
            isMulti={false}
            isClearable={true}
            defaultValue={defaultPaginateOptions?.form}
            formFieldTitle="form_id"
            error={formik.errors.form_id}
            onChange={handleSelectOption}
          />
          <FormSelectPaginate
            label="Resolve Form (Optional)"
            isMulti={false}
            isClearable={true}
            defaultValue={defaultPaginateOptions?.resolve_form}
            formFieldTitle="completion_form_id"
            error={formik.errors.completion_form_id}
            onChange={handleSelectOption}
          />
          <Button onClick={formik.submitForm} loading={isLoading} disabled={isLoading}>
            {isUpdating ? 'Update' : 'Create'} event type
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default IncidentTypeModal;
